import { FC, useCallback } from 'react';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import ExportDropdown from 'components/Shared/ExportDropdown';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import { useExportWorkshopsMutation } from 'redux/apiSlice/workshops.slice';
import { buildWorkshopsExportBody } from 'data/convertors/workshops.convertors';
import { TRtkErrorType } from 'data/types/generic.types';
import { TWorkshopExportProps } from './WorkshopExport.types';

const WorkshopExport: FC<TWorkshopExportProps> = ({
  selectedRowKeys,
  selectedFilters,
  sortColumn,
  columnKeys,
  disabled,
}) => {
  const [exportWorkshops, { isLoading: isExporting }] =
    useExportWorkshopsMutation();
  const { showToastNotification } = useShowToastNotification();

  // Visible columns in the order shown on screen, from the table settings.
  const getVisibleColumnKeys = useCallback((): string[] => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[ETableStoragePaths.WORKSHOPS] as
      | TColumnsState[]
      | undefined;
    if (columnsState?.length) {
      return columnsState.filter(col => col.show).map(col => String(col.key));
    }
    return columnKeys;
  }, [columnKeys]);

  const runExport = async (workshopIds?: string[]) => {
    try {
      await exportWorkshops(
        buildWorkshopsExportBody({
          filters: selectedFilters,
          sortColumn,
          visibleColumnKeys: getVisibleColumnKeys(),
          workshopIds,
        }),
      ).unwrap();
    } catch (err) {
      showToastNotification({
        message:
          (err as TRtkErrorType)?.data?.message ||
          'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  };

  const handleExportSelected = () => {
    if (!selectedRowKeys.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }
    runExport(selectedRowKeys.map(key => String(key)));
  };

  return (
    <ExportDropdown
      onExportAll={() => runExport()}
      onExportSelected={handleExportSelected}
      isExporting={isExporting}
      disabled={disabled}
    />
  );
};

export default WorkshopExport;
